/* eslint-disable max-len */
import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

import {
    UiApplicationIcon,
    UiDashboardIcon,
    UiHomeIcon,
    UiLeadsIcon,
    UiLoanStatusIcon,
    UiMegaphoneIcon,
    UiResourcesIcon,
} from '@octane/spark'
import getLeadsCount from 'api/leads/getLeadsCount'
import brandConfig from 'branding'
import { BoatIcon, RVIcon } from 'components/CrossoverNewAppIcons'
import CustomerAgent from 'components/CustomerAgent'
import Navbar from 'components/Navbar'
import { UrlMatchType } from 'components/Navbar/NavbarItem'
import TermsOfUseModal from 'components/TermsOfUseModal/TermsOfUseModal'
import { shouldDoRedirect } from 'containers/DealerRedirector/DealerRedirector'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import useDashboard from 'hooks/useDashboard'
import useUser from 'hooks/useUser'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import { LEADS_PAGE_ROUTE_PATH } from 'pages/LeadsPage'
import { getAlreadyAddedLenders } from 'pages/MyAccount/Lenders/utils'
import AssetType from 'state/enums/AssetType'
import getNewApplicationPath, {
    LEGACY_APPLICATION_PATH,
    NEW_MARINE_APPLICATION,
} from 'utils/getNewApplicationPath'

import LoggedInUser from './LoggedInUser/LoggedInUser'
import { shouldShowMenus } from './utils'

const MARINE_CONNECTION_DEALERSHIP_ID = 33640

const shouldShowSelectLenderPage = (userId: number) => {
    const lenders = getAlreadyAddedLenders(userId)

    return lenders.length > 1
}

const getSelectLenderRoute = () => {
    return '#!/application/new'
}

export default function NavbarContainer(this: unknown): JSX.Element | null {
    const [isOpen, setIsOpen] = useState(false)

    const {
        user,
        isLoggedIn,
        isAutoDealership,
        assetTypes,
        isMultiAssetDealership,
    } = useUser()

    const dealershipId = user?.dealership?.id
    const { hasDashboard } = useDashboard()
    const { pathname } = useLocation()
    const featureFlags = useFeatureFlagContext()

    const [newLeadsCount, setNewLeadsCount] = useState<number | string | null>(
        null,
    )

    const [showNotification, setShowNotification] = useState(true)
    const navigate = useNavigate()

    const handleLeadsPageClick = () => {
        navigate(LEADS_PAGE_ROUTE_PATH)
        setShowNotification(false)
    }

    const isPowersportsDealership =
        user?.dealership?.primaryAssetType === AssetType.POWERSPORTS_VEHICLE

    const isRVDealership =
        user?.dealership?.primaryAssetType === AssetType.RECREATIONAL_VEHICLE

    const dealershipHasMarineAssetType =
        assetTypes.includes(AssetType.MARINE) ?? false

    const isRVMarineCrossoverDealership =
        isRVDealership && dealershipHasMarineAssetType

    let newApplicationPath = getNewApplicationPath(
        featureFlags,
        user?.dealership?.primaryAssetType,
    )

    if (user) {
        if (shouldShowSelectLenderPage(user.id)) {
            newApplicationPath = getSelectLenderRoute()
        }
    }

    useEffect(() => {
        let isMounted = true

        async function fetchNewLeadsCount() {
            if (
                isLoggedIn &&
                featureFlags?.leadsPage &&
                isPowersportsDealership
            ) {
                try {
                    const leadsCount = await getLeadsCount()
                    const newLeads = leadsCount?.new_leads_count

                    if (isMounted) {
                        if (newLeads > 99) {
                            setNewLeadsCount('99+')
                        } else if (newLeads > 0) {
                            setNewLeadsCount(newLeads)
                        } else {
                            setNewLeadsCount(null)
                        }
                    }
                } catch (error) {
                    if (isMounted) {
                        OctaneLogger.error(
                            `Error fetching total leads count:`,
                            undefined,
                            error as Error,
                        )
                    }
                }
            }
        }

        fetchNewLeadsCount()

        return () => {
            isMounted = false
        }
    }, [isLoggedIn, featureFlags?.leadsPage, isPowersportsDealership])

    if (isLoggedIn === undefined) {
        return null
    }

    if (brandConfig.hideNavbarAtLogin && !isLoggedIn) {
        return null
    }

    if (isLoggedIn) {
        const redirectExpected = shouldDoRedirect(user?.dealerPortalUrl)

        if (redirectExpected === undefined || redirectExpected === true) {
            return null
        }
    }

    // Determine the status page link based on dealership type and feature flags
    const shouldUseNewStatusPage =
        (!isPowersportsDealership &&
            featureFlags?.useNewApplicationStatusPage) ||
        (isPowersportsDealership &&
            featureFlags?.useNewStatusPageForPowersports)

    const statusPageLink = shouldUseNewStatusPage
        ? '#/saved-applications'
        : '#!/saved-applications'

    return (
        <Navbar>
            <TermsOfUseModal
                isOpen={isOpen}
                handleClose={() => setIsOpen(false)}
            />
            {shouldShowMenus(pathname) && isLoggedIn ? (
                <>
                    {(!isAutoDealership || isMultiAssetDealership) && (
                        <Navbar.Item
                            id="menu-home"
                            href="#/"
                            icon={UiHomeIcon}
                            text="Home"
                            urlMatchType={UrlMatchType.Exact}
                        />
                    )}
                    {(hasDashboard || isAutoDealership) &&
                        !isMultiAssetDealership && (
                            <Navbar.Item
                                id="menu-dashboard"
                                href={
                                    featureFlags?.webAppLoyaltyDashboard ||
                                    isAutoDealership
                                        ? '#/dashboard'
                                        : '#!/loyalty-dashboard'
                                }
                                icon={UiDashboardIcon}
                                text="Dashboard"
                                urlMatchType={UrlMatchType.Exact}
                            />
                        )}
                    {featureFlags?.leadsPage &&
                        (isPowersportsDealership ||
                            dealershipId ===
                                MARINE_CONNECTION_DEALERSHIP_ID) && (
                            <div>
                                <Navbar.Item
                                    id="menu-leads"
                                    href={`#/${LEADS_PAGE_ROUTE_PATH}`}
                                    icon={UiLeadsIcon}
                                    text="Leads"
                                    urlMatchType={UrlMatchType.Exact}
                                    notificationCount={newLeadsCount}
                                    shouldShowNotification={showNotification}
                                    onClick={handleLeadsPageClick}
                                />
                            </div>
                        )}
                    {isRVMarineCrossoverDealership &&
                    !featureFlags?.showRVCrossoverExperience ? (
                        <>
                            <Navbar.Item
                                id="menu-new-rv-app"
                                href={LEGACY_APPLICATION_PATH}
                                icon={RVIcon}
                                text="New RV App"
                                urlMatchType={UrlMatchType.StartsWith}
                            />
                            <Navbar.Item
                                id="menu-new-marine-app"
                                href={NEW_MARINE_APPLICATION}
                                icon={BoatIcon}
                                text="New Marine App"
                                urlMatchType={UrlMatchType.StartsWith}
                                wider
                            />
                        </>
                    ) : (
                        <Navbar.Item
                            id="menu-new-app"
                            href={newApplicationPath}
                            icon={UiApplicationIcon}
                            text="New App"
                            urlMatchType={UrlMatchType.StartsWith}
                        />
                    )}

                    <Navbar.Item
                        id="menu-status"
                        href={statusPageLink}
                        icon={UiLoanStatusIcon}
                        text="Status"
                        urlMatchType={UrlMatchType.Exact}
                    />
                    <Navbar.Dropdown
                        id="menu-help"
                        icon={UiResourcesIcon}
                        text="Help"
                    >
                        <Navbar.Dropdown.UrlItem
                            text="Request 10-day Payoff"
                            hashNavigate="ten-day-payoff"
                        />
                        <Navbar.Dropdown.UrlItem
                            text="Contact Us"
                            url={
                                isAutoDealership
                                    ? 'https://www.roadrunnerfinancial.com/auto-dealer-contact'
                                    : 'https://www.roadrunnerfinancial.com/dealer-contact'
                            }
                        />
                        <Navbar.Dropdown.CustomItem
                            text="Terms of Use"
                            handleClick={() => setIsOpen(true)}
                        />
                    </Navbar.Dropdown>
                    <Navbar.Item
                        id="menu-feedback"
                        href="https://roadrunnerfinancial2ndorg.my.site.com/roadrunner/s/dealer-advocacy-feedback"
                        icon={UiMegaphoneIcon}
                        text="Feedback"
                        urlMatchType={UrlMatchType.Exact}
                        newTab
                    />
                    <CustomerAgent
                        agentName={user?.customerAgent?.agentName}
                        phoneNumber={user?.customerAgent?.agentPhoneNumber}
                        extension={user?.customerAgent?.agentExtension}
                        whiteLabelId={brandConfig.whitelabelId}
                    />
                    <LoggedInUser />
                </>
            ) : null}
        </Navbar>
    )
}
